<template>
    <div>
        <div class="container-fluid pt-4">
            <b-modal id="StoreAlertEdit" title="Admin" @ok="close()" ok-only>
                <p class="my-4">{{ message }}</p>
            </b-modal>
            <hr>
            <div class="row product-adding">
                <div class="col-xl-12">
                    <div class="row product-adding">
                        <div class="col-xl-6">
                            <div class="digital-add needs-validation">
                                <h4>Configuration for EPROLO</h4>
                                <p class="mb-4">
                                    Enter the API key and secret provided by EPROLO to import
                                    products directly into this store. EPROLO has a single
                                    environment (no sandbox): the same key is used for tests
                                    and live orders.
                                </p>
                                <div class="form-group">
                                    <b-form-checkbox v-model="form.eproloEnabled">Enable EPROLO product import?</b-form-checkbox>
                                </div>
                                <div class="form-group">
                                    <b-form-checkbox v-model="form.eproloShipping">Live EPROLO shipping rates at checkout?</b-form-checkbox>
                                    <small class="text-muted">The storefront shows the real EPROLO shipping methods and costs
                                        instead of the fixed shipping price (Store &gt; Shipping).</small>
                                    <small v-if="form.eproloShipping && form.bigbuyShipping" class="text-warning d-block">
                                        BigBuy live rates is also on: EPROLO takes priority at the checkout.
                                        Untick one of the two to avoid the ambiguity.
                                    </small>
                                </div>
                                <div class="form-group">
                                    <label class="col-form-label pt-0">API key (openApiKey)</label>
                                    <input class="form-control" type="text" v-model="form.eproloApiKey"
                                        placeholder="Key provided by the EPROLO support" />
                                </div>
                                <div class="form-group">
                                    <label class="col-form-label pt-0">API secret (openApiSecret)</label>
                                    <input class="form-control" type="text" v-model="form.eproloApiSecret"
                                        placeholder="Secret provided by the EPROLO support" />
                                </div>
                                <div class="form-group">
                                    <label class="col-form-label pt-0">Margin (%) applied on wholesale price</label>
                                    <input class="form-control" type="number" min="0" step="1" v-model.number="form.eproloMargin" />
                                    <small class="text-muted">Imported price = (product cost + EU shipping below) × (1 + this margin / 100).</small>
                                </div>
                                <div class="form-group">
                                    <label class="col-form-label pt-0">Worst-case EU shipping added to the price</label>
                                    <input class="form-control" type="number" min="0" step="0.01" v-model.number="form.eproloShippingEU" />
                                    <small class="text-muted">Highest EPROLO shipping cost among your European destinations.
                                        It is added to the product cost before the margin, so every EU order is covered —
                                        then enable Free shipping (Store &gt; Shipping): the customer pays no separate shipping.</small>
                                    <small v-if="form.eproloShipping && form.eproloShippingEU > 0" class="text-warning d-block">
                                        Live EPROLO shipping rates at checkout is also on: the customer would pay the
                                        shipping twice. Leave this at 0 or untick the live rates option.
                                    </small>
                                </div>
                                <div class="form-group mb-0">
                                    <button type="button" class="btn btn-outline-primary" @click="testConnection"
                                        :disabled="testing">
                                        {{ testing ? "Testing..." : "Test connection" }}
                                    </button>
                                    <p v-if="testMessage" class="mt-3 mb-0" :class="testOk ? 'text-success' : 'text-danger'">
                                        {{ testMessage }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="col-xl-6">
                            <div class="digital-add needs-validation">
                                <h4>Import products</h4>
                                <p>
                                    Once the key and secret are saved, open the import page to browse
                                    the EPROLO catalog and add products to this store. Each imported
                                    product is also pushed to your EPROLO account (EPROLO requires it
                                    before an order can be placed).
                                </p>
                                <router-link to="/products/eprolo-import" class="btn btn-secondary">
                                    Open EPROLO import page
                                </router-link>
                                <p class="mt-4 mb-1">Active API endpoint: <code>https://openapi.eprolo.com</code></p>
                                <p class="mb-1">
                                    API key:
                                    <strong :class="form.eproloApiKey ? 'text-success' : 'text-muted'">
                                        {{ form.eproloApiKey ? "saved" : "missing" }}
                                    </strong>
                                </p>
                                <p class="mb-0">
                                    API secret:
                                    <strong :class="form.eproloApiSecret ? 'text-success' : 'text-muted'">
                                        {{ form.eproloApiSecret ? "saved" : "missing" }}
                                    </strong>
                                </p>
                                <p class="mt-4 mb-0">
                                    <router-link to="/stores/bigbuy" class="small">BigBuy settings</router-link>
                                    <span class="mx-1 text-muted">·</span>
                                    <span class="small text-muted">Each store picks its dropshipping provider
                                        (BigBuy or EPROLO) on this page.</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-xl-12 pb-3 pt-3">
                    <div class="form-group mb-0">
                        <div class="product-buttons text-right">
                            <button type="button" class="btn btn-primary" @click="save">
                                Save
                            </button>
                        </div>
                    </div>
                </div>
                <div class="col-xl-12 pb-4">
                    <div class="digital-add needs-validation">
                        <h4>Scheduled stock sync (cron)</h4>
                        <p class="mb-2">
                            Add this line to the VPS crontab (<code>crontab -e</code>) to refresh the
                            stock and the variants of the EPROLO products imported into
                            <strong>{{ form.host }}</strong> every day. It runs on the VPS itself,
                            against the local Hermes API (port 7001). Prices are never changed.
                        </p>
                        <pre class="cron-command mb-2"><code>{{ cronCommand }}</code></pre>
                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="copyCron">
                            {{ copied ? "Copied!" : "Copy command" }}
                        </button>
                        <small class="text-muted d-block mt-2">
                            This line targets {{ form.host }} only — switch the store in the top bar
                            to get the same cron command for another store.
                        </small>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import firebase from '@/utils/firestore-shim';
import Cookies from 'js-cookie';
import axios from 'axios';
import { apiUrl, uploadHeaders } from '../../../utils/imageUpload.js';
import config from '../../../../config.json';
export default {
    data() {
        return {
            form: {},
            data: {},
            id: '',
            message: "",
            error: 0,
            testing: false,
            testOk: false,
            testMessage: "",
            apiKey: config.apiKey || "",
            copied: false
        };
    },
    async mounted() {
        var data = await this.getStoresData();
        var store = data && data.length ? data[0] : null;
        if (!store) {
            this.$router.push('/dashboard/default');
            return;
        }
        // Defaults are set BEFORE the object is assigned to this.form, so the
        // new fields are reactive (Vue 2 cannot detect properties added later).
        if (store.eproloEnabled === undefined || store.eproloEnabled === null) store.eproloEnabled = false;
        if (store.eproloShipping === undefined || store.eproloShipping === null) store.eproloShipping = false;
        if (store.eproloApiKey === undefined || store.eproloApiKey === null) store.eproloApiKey = '';
        if (store.eproloApiSecret === undefined || store.eproloApiSecret === null) store.eproloApiSecret = '';
        var margin = Number(store.eproloMargin);
        store.eproloMargin = (store.eproloMargin === undefined || store.eproloMargin === null ||
            store.eproloMargin === '' || !isFinite(margin)) ? 30 : margin;
        var shippingEU = Number(store.eproloShippingEU);
        store.eproloShippingEU = (store.eproloShippingEU === undefined || store.eproloShippingEU === null ||
            store.eproloShippingEU === '' || !isFinite(shippingEU)) ? 0 : shippingEU;
        this.form = store;
    },
    computed: {
        // Full crontab line for the CURRENT store: this page is per-store
        // (hostCookie), so switching the store switches the command shown.
        cronCommand() {
            var host = this.form.host || 'YOUR-STORE.com';
            var key = this.apiKey || 'YOUR_HERMES_API_KEY';
            return '17 4 * * * curl -s -X POST -H "x-api-key: ' + key + '" ' +
                'http://127.0.0.1:7001/api/stores/' + host + '/eprolo/sync >> /var/log/eprolo-sync.log 2>&1';
        }
    },
    methods: {
        async save() {
            if ((this.form.title == "") || (this.form.host == "") || (this.form.logo == "") || (this.form.symbol == "") || (this.form.rate == "")) {
                this.message = "Please complete all required fields!";
                this.error = 1;
                this.$bvModal.show('StoreAlertEdit');
                return false;
            } else {
                this.error = 0;
            }
            var margin = Number(this.form.eproloMargin);
            this.form.eproloMargin = (this.form.eproloMargin === '' || !isFinite(margin)) ? 0 : margin;
            var shippingEU = Number(this.form.eproloShippingEU);
            this.form.eproloShippingEU = (this.form.eproloShippingEU === '' || !isFinite(shippingEU) || shippingEU < 0) ? 0 : shippingEU;
            if (this.form.eproloEnabled && (!this.form.eproloApiKey || !this.form.eproloApiSecret)) {
                this.message = "The API key and secret are required when EPROLO is enabled.";
                this.error = 1;
                this.$bvModal.show('StoreAlertEdit');
                return false;
            }
            let ref = await firebase.firestore().collection('Sites').doc(this.form.host).set(this.form)
                .then(() => {
                    this.message = "Document successfully written!";
                    this.error = 0;
                    this.$bvModal.show('StoreAlertEdit');
                })
                .catch((error) => {
                    this.message = "Error writing document: " + error;
                    this.error = 1;
                    this.$bvModal.show('StoreAlertEdit');
                });
        },
        // Tests the SAVED settings through the Hermes API (the EPROLO key and
        // secret never reach the browser): GET /api/stores/{host}/eprolo/test.
        // Save the form before testing so the server sees the saved key.
        async testConnection() {
            this.testOk = false;
            this.testMessage = "";
            if (!this.form.host) {
                this.testMessage = "No store selected.";
                return false;
            }
            this.testing = true;
            try {
                var res = await axios.get(apiUrl + '/api/stores/' + encodeURIComponent(this.form.host) + '/eprolo/test', {
                    headers: uploadHeaders(),
                    timeout: 20000
                });
                var count = res.data && res.data.categories ? res.data.categories : 0;
                this.testOk = true;
                this.testMessage = "Connection successful" +
                    (count > 0 ? " — " + count + " catalog categories available." : ".");
            } catch (error) {
                this.testOk = false;
                var detail = error && error.response && error.response.data && error.response.data.error
                    ? String(error.response.data.error) : "";
                var status = error.response ? error.response.status : 0;
                if (status === 401) {
                    this.testMessage = detail || "Hermes API key invalid or missing (x-api-key). Check config.json.";
                } else if (status === 409) {
                    this.testMessage = detail || "EPROLO is disabled or the key/secret is missing. Save the settings first.";
                } else if (status === 502 || status === 504) {
                    this.testMessage = detail || "EPROLO returned an error or did not answer in time.";
                } else if (status) {
                    this.testMessage = detail || "Request returned HTTP " + status + ".";
                } else {
                    this.testMessage = "Unable to reach the Hermes API (" + (error.message || "network error") + ").";
                }
            } finally {
                this.testing = false;
            }
        },
        // Copies the crontab line (secure-context clipboard API, with an
        // execCommand fallback for older browsers).
        async copyCron() {
            var done = () => {
                this.copied = true;
                setTimeout(() => { this.copied = false; }, 2000);
            };
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(this.cronCommand);
                } else {
                    var area = document.createElement('textarea');
                    area.value = this.cronCommand;
                    area.style.position = 'fixed';
                    area.style.opacity = '0';
                    document.body.appendChild(area);
                    area.select();
                    document.execCommand('copy');
                    document.body.removeChild(area);
                }
                done();
            } catch (error) {
                // Copy refused: the line stays selectable in the block.
            }
        },
        close() {
            this.$bvModal.hide('StoreAlertEdit');
        },
        async getStoresData() {
            let listData = [];
            var ProductRef = await firebase.firestore().collection("Sites")
                .where("host", "==", this.getStore())
                .get()
                .then((querySnapshot) => {
                    querySnapshot.forEach((doc) => {
                        this.id = doc.id;
                        listData.push(
                            doc.data()
                        );
                    });
                });
            return listData;

        },
        getStore() {
            const host = Cookies.get('hostCookie');
            if (host) {
                return host;
            } else {
                this.$router.push('/dashboard/default');
            }
        }
    }
};
</script>
<style lang="scss" scoped>
pre.cron-command {
    background: #f5f6f8;
    border: 1px solid #e3e6ea;
    border-radius: 4px;
    padding: 12px 14px;
    white-space: pre-wrap;
    word-break: break-all;
    margin-bottom: 0;
}
</style>
